import { LabelContent } from 'V2Components'
import { useAppDetailsContext } from 'features/snowflake'
import { createDataSelectorHook } from 'infra/redux'
import { Label, TargetIcon, Typography } from 'procyon-ui'
import React, { useEffect, useMemo, useState } from 'react'
import _ from 'lodash'
import { reduxApiClient } from 'infra'

const useSlices = createDataSelectorHook([
  'snowFlakeAccounts',
  'snowFlakeUsers',
  'snowFlakeRoles',
  'policyList',
  'userList'
])

const UsersTab = () => {
  const { app } = useAppDetailsContext()
  const { slices } = useSlices()
  const [usersList, setUsersList] = useState([])
  const [selectedUser, setSelectedUser] = useState({})
  const [puserList, setPUserList] = useState([])
  const [updateState, setUpdateState] = useState(false)
  const [selectedUserGrantedObj, setSlectedUserGrantedObj] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    initialApiCall()
  }, [slices])

  const initialApiCall = async () => {
    await reduxApiClient('snowflakeusers').getAll({})
    await reduxApiClient('pacpolicys').getAll({})
  }

  useMemo(() => {
    let procyonUserMap = []
    const filterCurrentAccount = slices.snowFlakeAccounts.find(
      (account) => account.Application.RefID === app.ObjectMeta.ID
    )

    const filterUsers = slices.snowFlakeUsers.filter(
      (role) => role.Spec.SnowflakeAccount.RefID === filterCurrentAccount?.ObjectMeta?.ID
    )

    filterUsers.filter((user) => {
      procyonUserMap.push(slices.userList.find((users) => users.Spec.EmailID === user.Spec.Email))
    })

    setPUserList(procyonUserMap)
    setUsersList(filterUsers)
    setSelectedUser(filterUsers[0])
  }, [])

  useEffect(() => {
    getSelectedUserGrantedRole()
  }, [selectedUser, updateState])

  useEffect(() => {
    setTimeout(() => {
      setUpdateState(true)
    }, 2000)
  }, [])

  const handleUserClick = (user) => {
    setSelectedUser(user)
  }

  const getSnowFlakePolicyList = () =>
    slices.policyList.filter((policy) => {
      return policy?.Spec?.ActionMap?.Snowflake?.PolicyRule
    })

  const getExpiryFormatedTime = (time) => {
    const date = new Date(time)

    const options = { timeZone: 'Asia/Kolkata', hour12: false }
    const istDate = date.toLocaleString('en-US', options).replace(',', '')

    // Split the date and time for custom formatting
    const [datePart, timePart] = istDate.split(' ')
    const formattedDate = datePart.split('/').reverse().join('-') + ' ' + timePart

    return formattedDate
  }

  const getSelectedUserGrantedRole = async () => {
    setIsLoading(true)
    try {
      const snowflakeuserList = await reduxApiClient('snowflakeusers').getAll({})
      const selectedUserGrantObj = _.find(snowflakeuserList, {
        Spec: { Email: selectedUser?.Spec?.Email }
      })
      setSlectedUserGrantedObj(selectedUserGrantObj?.Spec?.GrantedRoles)
      getGrantedRoles()
    } catch (error) {
      console.error('Error fetching granted roles:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getGrantedRoles = () => {
    const grantedRolesSet = new Set()
    const grantedRolesArray = []
    const filteredPuser = puserList.find((user) => user.Spec.EmailID === selectedUser?.Spec?.Email)
    selectedUserGrantedObj?.ObjectRef?.forEach((objref) => {
      const filteredRoles = slices.snowFlakeRoles.filter(
        (role) => role.ObjectMeta.ID === objref.RefID
      )

      filteredRoles.forEach((role) => {
        if (!grantedRolesSet.has(role.ObjectMeta.ID)) {
          grantedRolesSet.add(role.ObjectMeta.ID)
          grantedRolesArray.push(role)
        }
      })
    })

    // check if granted roles refid is exist in pacpolicy
    const snowflakePolicy = getSnowFlakePolicyList()

    const roleLabels = grantedRolesArray.map((role) => {
      const rolePolicies = snowflakePolicy.filter((policy) => {
        const actionMap = policy?.Spec?.ActionMap?.Snowflake?.PolicyRule
        const policyIssuedTo = policy?.IssuedTo?.ObjectRef

        return actionMap.some((pobj) => {
          const ispolicyIssuedTouser = policyIssuedTo.find(
            (issuedto) => issuedto.RefID === filteredPuser.ObjectMeta.ID
          )
          return ispolicyIssuedTouser && pobj.ObjectRef.RefID === role.ObjectMeta.ID
        })
      })

      if (rolePolicies.length > 0) {
        // Get the expiry dates of the policies for the role
        const expiryDates = rolePolicies.map((policy) => policy.NotAfter).join(', ')
        return (
          <Label
            key={role.ObjectMeta.ID}
            variant='grayBlue'
            style={{ marginBottom: '5px' }}
            text={
              <div className='flex items-center'>
                {role.Spec.Name} -{' '}
                {expiryDates && (
                  <span className='text-[red] font-medium ml-1'>
                    Expiry - {getExpiryFormatedTime(expiryDates)}
                  </span>
                )}
              </div>
            }
          />
        )
      }
    })

    return roleLabels
  }

  return (
    <>
      <div className='flex'>
        <div className='w-1/2 border-r border[#D8DDE4] pr-8'>
          {usersList &&
            usersList.map((user) => (
              <div className='my-5'>
                <div
                  className='flex justify-between items-center border p-2 cursor-pointer'
                  onClick={() => handleUserClick(user)}
                >
                  <div className='flex gap-2'>
                    <TargetIcon type={'USER'} />
                    <span className='' style={{ width: '250px', wordBreak: 'break-word' }}>
                      <Typography variant='body-regular'>{user.Spec.Email}</Typography>
                    </span>
                  </div>
                </div>
              </div>
            ))}
        </div>
        {usersList.length > 0 && (
          <div className='w-1/2 pr-8'>
            <Typography variant='h3' style={{ padding: '20px 30px' }}>
              Access Details of <span className='font-semibold'>{selectedUser?.Spec?.Email}</span>
            </Typography>
            <div>
              <LabelContent
                title='Default Roles'
                className='pl-7'
                content={
                  <Label
                    variant='grayBlue'
                    text={
                      <div className='flex items-center'>{selectedUser?.Spec?.DefaultRole}</div>
                    }
                  />
                }
              />
              <LabelContent
                title='Default Warehouse'
                className='pl-7'
                content={
                  <Label
                    variant='grayBlue'
                    text={
                      <div className='flex items-center'>
                        {selectedUser?.Spec?.DefaultWarehouse}
                      </div>
                    }
                  />
                }
              />

              <LabelContent
                className='pl-7'
                title='Granted Roles'
                content={
                  isLoading ? (
                    <Label
                      variant='grayBlue'
                      text={<div className='flex items-center'>Loading granted roles...</div>}
                    />
                  ) : (
                    getGrantedRoles()
                  )
                }
              />
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export { UsersTab }
